import React, { useMemo, useState, useEffect, useRef } from 'react';
import { FormattedMessage } from '../../../util/reactIntl';
import { PrimaryButton } from '../../../components';
import DeliveryReturnSelectField from '../../CartShared/DeliveryReturnSelectField';
import PickupLocationMap from '../DeliveryPickupOptionsField/PickupLocationMap';
import CartAddonsField from '../../CartShared/CartAddonsField';
import EstimatedCustomerBreakdownMaybe from '../../OrderPanel/EstimatedCustomerBreakdownMaybe';
import { BOOKING_PROCESS_NAME } from '../../../transactions/transaction';
import { calculateSubTotal, calculateCustomerTotal } from '../../../util/cartHelpers';
import moment from 'moment-timezone/builds/moment-timezone-with-data-10-year-range.min';
import css from './CartModalFooter.module.css';
import classNames from 'classnames';
import throttle from 'lodash/throttle';

const CartModalFooter = props => {
    const {
        items,
        intl,
        form,
        formValues,
        deliveryPickup,
        addons,
        onRemoveGlobalOptionalFee,
        onRemoveGlobalUsageFee,
        onRentNow,
        fetchLineItemsInProgress,
        lineItems,
        listing,
        currentUserCurrency,
        exchangeRates,
        marketplaceName,
        marketplaceCurrency,
        deliveryPickupError,
        deliveryPickupRef,
        isOwner = false,
        showAddressInStore = false,
        // The group's Start/End date fields with their inline picker (CartDatesEditor,
        // rendered by CartModal) - the panel's own way to correct an order's dates.
        // While that picker is open, Rent Now is held until the shopper applies or
        // discards (same as CartDrawer's checkout button).
        isEditingDates = false,
        // When false the Rent Now bar is NOT rendered here - CartModal renders
        // <CartModalCta> itself, pinned below its single scroll region, so this
        // component is just the scrolling Order Summary content. Defaults to true so
        // any other consumer keeps the self-contained footer.
        renderCta = true,
    } = props;

    const [deliveryPickupExpanded, setDeliveryPickupExpanded] = useState(true);
    const ctaSectionRef = useRef();

    // Get delivery/pickup info from first item's listing (all items should be from same store)
    const firstItem = items?.[0];
    const listingForDeliveryPickup = listing || firstItem?.listing;

    // `deliveryPickup` is a config object that's non-null for any non-empty cart, even
    // when none of its items actually has a delivery/pickup fee configured (see
    // getCartDeliveryPickupConfig in cartModalHelpers.js) - it is NOT the same as "there
    // is something to show here". Mirrors the exact condition DeliveryReturnSelectField
    // uses internally to decide whether to render anything, so the heading/toggle can't
    // show with genuinely nothing underneath them.
    const {
        hasDeliveryOption,
        hasPickupOption,
        perItemDeliveryFees = [],
        perItemPickupFees = [],
    } = deliveryPickup || {};
    const hasDeliveryPickupOptions = !!(
        hasDeliveryOption ||
        hasPickupOption ||
        perItemDeliveryFees.length ||
        perItemPickupFees.length
    );

    // Calculate pricing breakdown
    const subtotal = useMemo(() => {
        if (!lineItems) return null;
        return calculateSubTotal(lineItems, currentUserCurrency, intl, exchangeRates);
    }, [lineItems, currentUserCurrency, intl, exchangeRates]);

    const total = useMemo(() => {
        if (!lineItems) return null;
        return calculateCustomerTotal(lineItems, currentUserCurrency, intl, exchangeRates);
    }, [lineItems, currentUserCurrency, intl, exchangeRates]);


    const breakdownData = useMemo(() => {
        if (!firstItem?.bookingDates) return null;
        return {
            startDate: moment(firstItem.bookingDates.startDate).tz(firstItem.timeZone).toDate(),
            endDate: moment(firstItem.bookingDates.endDate).tz(firstItem.timeZone).toDate(),
        };
    }, [firstItem]);

    // Throttled scroll handler for CTA section sticky state - only for the
    // self-contained (renderCta) layout, where the CTA lives inside this scrolling
    // footer and is made sticky by the is-stuck class as the overlay scrolls.
    useEffect(() => {
        if (!renderCta) return undefined;
        const handleScroll = throttle(() => {
            if (!ctaSectionRef.current) return;
            
            // Find the overlay element (parent with scroll)
            const overlayElement = ctaSectionRef.current.closest('.cartModal_overlay, [class*="overlay"]');
            if (!overlayElement) return;
            
            const rect = ctaSectionRef.current.getBoundingClientRect();
            const overlayRect = overlayElement.getBoundingClientRect();
            
            // Check if CTA section is at the bottom of the visible overlay area
            const ctaBottom = rect.bottom - overlayRect.top;
            const overlayHeight = overlayElement.clientHeight;
            
            // If the bottom of CTA is at or beyond the visible bottom of overlay, it's "stuck"
            if (ctaBottom >= overlayHeight - 10) {
                ctaSectionRef.current.classList.add('is-stuck');
            } else {
                ctaSectionRef.current.classList.remove('is-stuck');
            }
        }, 100, { leading: true, trailing: true });

        // Find the overlay element and attach scroll listener
        const overlayElement = document.querySelector('.cartModal_overlay, [class*="overlay"]');
        if (overlayElement) {
            overlayElement.addEventListener('scroll', handleScroll, { passive: true });
            handleScroll(); // Initial check
            return () => {
                handleScroll.cancel();
                overlayElement.removeEventListener('scroll', handleScroll);
            };
        }
        
        // Fallback to window scroll if overlay not found
        window.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll(); // Initial check
        return () => {
            handleScroll.cancel();
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    return (
        <div className={css.footer}>
            {/* Delivery & Pickup Options (uses parent FinalForm context) - only when at
                least one item actually has a delivery/pickup fee configured, otherwise
                this heading/toggle would show with nothing underneath them. */}
            {hasDeliveryPickupOptions && (
                <div className={css.deliveryPickupSectionContainer}>
                    <h5 className={css.deliveryTitle}>Delivery & Pickup Options</h5>
                    <button type="button" className={css.deliveryPickupToggle} onClick={() => setDeliveryPickupExpanded(e => !e)}>
                        {deliveryPickupExpanded ? <i className='ri-arrow-up-s-line' />:
                        <i className='ri-arrow-down-s-line' /> }
                    </button>

                    <div className={classNames(css.deliveryPickupSection, deliveryPickupExpanded ? css.expanded : null)}>
                        {/* Same shared dropdown widget the Listing Page cart drawer uses -
                            see the cart-consistency plan for why this replaced the
                            checkbox-based CartModal-only DeliveryPickupOptionsField. */}
                        <div ref={deliveryPickupRef}>
                            <DeliveryReturnSelectField
                                intl={intl}
                                fetchLineItemsInProgress={fetchLineItemsInProgress}
                                values={formValues}
                                deliveryPickup={deliveryPickup}
                                deliveryPickupError={deliveryPickupError}
                                form={form}
                            />
                        </div>

                        {/* Store Profile-only pickup-location map preview - intentionally
                            not part of the shared widget above (see cart-consistency
                            plan: the Listing Page cart drawer has no map yet). */}
                        <PickupLocationMap
                            listing={listingForDeliveryPickup}
                            values={formValues}
                            deliveryPickup={deliveryPickup}
                            showAddressInStore={showAddressInStore}
                        />
                    </div>
                </div>
            )}

            {/* Shared (oncePerCart) add-ons - same component CartDrawer uses, rendered
                once for the whole cart rather than once per item (see
                CartModalItemFields.js's isOncePerCartFee filter). Deliberately outside
                the "Delivery & Pickup Options" section above and self-gated on whether
                there's anything selected/required (see its own hasAddons check) - a
                shared add-on isn't a delivery/pickup concern, and can exist even when
                this listing has no delivery/pickup fee configured at all. */}
            <CartAddonsField
                intl={intl}
                disabled={fetchLineItemsInProgress}
                values={formValues}
                addons={addons}
                onRemoveOptionalFee={onRemoveGlobalOptionalFee}
                onRemoveUsageFee={onRemoveGlobalUsageFee}
                className={css.addonsSection}
            />

            {/* Pricing Breakdown */}
            {lineItems && (
                <div className={css.pricingSection}>
                    <EstimatedCustomerBreakdownMaybe
                        breakdownData={breakdownData}
                        lineItems={lineItems}
                        timeZone={firstItem?.timeZone}
                        currency={marketplaceCurrency || currentUserCurrency}
                        marketplaceName={marketplaceName}
                        processName={BOOKING_PROCESS_NAME}
                        isShowCommissionLineItem={false}
                    />
                </div>
            )}

            {/* Rent Now / Generate Quote Button */}
            {renderCta ? (
                <div className={css.ctaSection} ref={ctaSectionRef}>
                    <CartModalCta
                        onRentNow={onRentNow}
                        fetchLineItemsInProgress={fetchLineItemsInProgress}
                        lineItems={lineItems}
                        isEditingDates={isEditingDates}
                    />
                </div>
            ) : null}
        </div>
    );
};

/**
 * The Rent Now button on its own, so CartModal can pin it below its scroll region
 * (the same header / scrolling order / pinned CTA structure as the listing page's
 * CartDrawer) while the rest of the footer scrolls with the item rows.
 */
export const CartModalCta = ({ onRentNow, fetchLineItemsInProgress, lineItems, isEditingDates, hasExpiredDates }) => (
    <PrimaryButton
        onClick={onRentNow}
        inProgress={fetchLineItemsInProgress}
        // hasExpiredDates: the cart's start day has passed - CartModal shows the
        // expired notice above the date editor; submitting could only fail at checkout.
        disabled={!lineItems || fetchLineItemsInProgress || isEditingDates || hasExpiredDates}
        className={css.rentButton}
    >
        <FormattedMessage id="CartModal.rentNow" />
    </PrimaryButton>
);

export default CartModalFooter;
